import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import UniversalModal from '../Common/UniversalModal';
import UnsavedChangesPopover from '../Common/UnsavedChangesPopover';
import OrganizerForm from '../Organizers/OrganizerForm';
import Pagination from '../Common/Pagination';
import Toast from '../Common/Toast';
import '../Common/CardActions.css';
import '../Common/EntityCards.css';
import '../Common/PrimaryActionButton.css';
import OwnerBadge from '../Common/OwnerBadge';
import { useAuth } from '../../context/AuthContext';

const ManagersList = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalState, setModalState] = useState({ isOpen: false, mode: null, item: null });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'arbitrage_manager',
    inn: '',
    contacts: '',
    sro_info: '',
    phone: '',
    email: '',
    mailing_address: '',
    snils: ''
  });
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Удалить',
    cancelText: 'Отмена',
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiService.getUiSettings();
        const v = Number(resp?.data?.table_page_size);
        if ([12, 24, 48, 96].includes(v)) {
          setItemsPerPage(v);
          setCurrentPage(1);
        }
      } catch (_) {
        // ignore
      }
    })();
  }, []);

  const load = async () => {
    try {
      setLoading(true); setError('');
      const all = [];
      let page = 1;
      while (true) {
        const resp = await apiService.getManagers(`?page=${page}&page_size=96`);
        const chunk = resp.data?.results || resp.data || [];
        all.push(...chunk);
        if (!resp.data?.next) break;
        page += 1;
        if (page > 200) break;
      }
      setItems(all);
    } catch (err) {
      setError('Ошибка при загрузке: ' + (err.response?.data?.detail || err.message));
    } finally { setLoading(false); }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  const emptyManager = {
    name: '',
    type: 'arbitrage_manager',
    inn: '',
    contacts: '',
    sro_info: '',
    phone: '',
    email: '',
    mailing_address: '',
    snils: ''
  };

  const resetForm = () => {
    setFormData(emptyManager);
    setHasUnsavedChanges(false);
  };

  const prefillForm = (item) => {
    if (!item) {
      setFormData(emptyManager);
      setHasUnsavedChanges(false);
      return;
    }

    setFormData({
      name: item.name || '',
      type: 'arbitrage_manager',
      inn: item.inn || '',
      contacts: item.contacts || '',
      sro_info: item.sro_info || '',
      phone: item.phone || '',
      email: item.email || '',
      mailing_address: item.mailing_address || '',
      snils: item.snils || ''
    });
    setHasUnsavedChanges(false);
  };

  const handleAdd = () => { resetForm(); setModalState({ isOpen: true, mode: 'create', item: null }); };
  const handleView = (item) => { prefillForm(item); setModalState({ isOpen: true, mode: 'view', item }); };
  const handleEdit = (item) => { prefillForm(item); setModalState({ isOpen: true, mode: 'edit', item }); };
  const handleCancel = () => {
    if (modalState.mode === 'edit' && !hasUnsavedChanges) {
      setModalState({ ...modalState, mode: 'view' });
      setHasUnsavedChanges(false);
      return;
    }
    setModalState({ isOpen: false, mode: null, item: null });
    resetForm();
  };

  useEffect(() => {
    if (!modalState.isOpen || modalState.mode !== 'edit') return;
    const handleEsc = (e) => {
      if (e.key !== 'Escape') return;
      try {
        if (document.body.classList.contains('aot-searchable-select-open')) {
          e.preventDefault();
          e.stopPropagation();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();
          document.dispatchEvent(new Event('aot-close-searchable-select'));
          return;
        }
      } catch (_) {}
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      handleCancel();
    };
    document.addEventListener('keydown', handleEsc, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEsc, { capture: true });
    };
  }, [modalState.isOpen, modalState.mode, handleCancel]);

  const handleSaveManager = async (payload, { exit } = {}) => {
    try {
      let savedManager = null;

      if (modalState.item) {
        const response = await apiService.updateManager(modalState.item.id, {
          ...payload,
          type: 'arbitrage_manager'
        });
        savedManager = response.data || { ...modalState.item, ...payload, type: 'arbitrage_manager' };
        setItems(items.map(o => (o.id === savedManager.id ? savedManager : o)));
        showToast('Изменения сохранены');
      } else {
        const response = await apiService.createManager({
          ...payload,
          type: 'arbitrage_manager'
        });
        savedManager = response.data;
        setItems([savedManager, ...items]);
        showToast('Управляющий создан');
      }

      setHasUnsavedChanges(false);

      if (exit) {
        handleCancel();
      } else {
        prefillForm(savedManager);
        setModalState({ isOpen: true, mode: 'edit', item: savedManager });
      }

      return savedManager;
    } catch (err) {
      const data = err.response?.data;
      const message = data?.inn
        ? Array.isArray(data.inn) ? data.inn.join(', ') : data.inn
        : (data?.detail || err.message);
      showToast(`Ошибка при сохранении управляющего: ${message}`, 'error');
      return false;
    }
  };

  const handleDelete = (item) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Внимание: каскадное удаление',
      message:
        `Вы уверены, что хотите удалить управляющего ${item.name}?\n\n` +
        `Важно: вместе с управляющим будут удалены карточки торгов, в которых он указан.\n` +
        `Это действие нельзя отменить.`,
      onConfirm: async () => {
        try {
          await apiService.deleteManager(item.id);
          showToast('Управляющий удален');
          load();
        } catch (err) {
          setError('Ошибка при удалении: ' + (err.response?.data?.detail || err.message));
        } finally {
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        }
      },
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage]);

  const filtered = React.useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return items;
    return (items || []).filter((m) => {
      const hay = `${m.name || ''} ${m.inn || ''} ${m.snils || ''} ${m.phone || ''} ${m.email || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage, items]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const duplicateInnMeta = React.useMemo(() => {
    const byInn = new Map();
    (items || []).forEach((item) => {
      const inn = String(item?.inn || '').trim();
      if (!inn) return;
      if (!byInn.has(inn)) {
        byInn.set(inn, { owners: new Set(), count: 0 });
      }
      const meta = byInn.get(inn);
      meta.count += 1;
      const ownerKey = String(item?.owner_email || '').trim() || `no-owner-${item?.id}`;
      meta.owners.add(ownerKey);
    });
    return byInn;
  }, [items]);
  const paged = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);
  if (loading) return <div style={{ padding: 50, textAlign: 'center' }}><h2>Загрузка управляющих...</h2></div>;

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
        <h1>👤 Управляющие</h1>
        <button onClick={handleAdd} className="primary-create-button">
          <span className="primary-create-button__icon">+</span>
          Добавить
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по управляющим, ИНН, СНИЛС..."
          style={{ flex: 1, minWidth: 260, padding: 10, border: '1px solid #dee2e6', borderRadius: 10 }}
        />
        <div style={{ color: '#6c757d', whiteSpace: 'nowrap' }}>
          Найдено: <b>{totalItems}</b>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee', border: '1px solid #fcc', color: '#a00', padding: 15, borderRadius: 4, marginBottom: 20 }}>
          <span>{error}</span>
        </div>
      )}

      {paged.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 50, color: '#666', background: '#fff', borderRadius: 8, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <h3>Управляющие не найдены</h3>
          <p>Добавьте первого управляющего через кнопку «Добавить»</p>
        </div>
      ) : (
        <div className="entity-cards-grid">
          {paged.map((m) => (
            <div key={m.id} className="entity-card clickable" onClick={() => handleView(m)}>
              <div className="entity-card-header">
                <div className="entity-card-title-block">
                  <h3 className="entity-card-title">{m.name}</h3>
                  <OwnerBadge email={m.owner_email} />
                </div>
                <div className="card-actions">
                  <button
                    type="button"
                    className="card-action-btn card-action-btn--edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(m);
                    }}
                    title="Редактировать"
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="card-action-btn card-action-btn--delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(m);
                    }}
                    title="Удалить"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              <div className="entity-card-body">
                {m.inn && (
                  <div className="entity-info-row">
                    <span className="entity-label">ИНН:</span>
                    <span className="entity-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{m.inn}</span>
                      {(() => {
                        if (!isAdmin) return null;
                        const meta = duplicateInnMeta.get(String(m.inn).trim());
                        if (!meta || meta.owners.size < 2) return null;
                        return (
                          <span
                            title={`ИНН используется у ${meta.owners.size} пользователей, записей: ${meta.count}`}
                            style={{
                              fontSize: 12,
                              color: '#8a5a00',
                              background: '#fff3cd',
                              border: '1px solid #ffe69c',
                              borderRadius: 999,
                              padding: '2px 8px',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Дубль у пользователей: {meta.owners.size}
                          </span>
                        );
                      })()}
                    </span>
                  </div>
                )}
                {m.snils && (
                  <div className="entity-info-row">
                    <span className="entity-label">СНИЛС:</span>
                    <span className="entity-value">{m.snils}</span>
                  </div>
                )}
                {(m.phone || m.email) && (
                  <div className="entity-info-row">
                    <span className="entity-label">Контакты:</span>
                    <span className="entity-value">
                      {m.phone ? `тел. ${m.phone}` : ''}
                      {m.phone && m.email ? ' · ' : ''}
                      {m.email || ''}
                    </span>
                  </div>
                )}
                {m.mailing_address && (
                  <div className="entity-info-row">
                    <span className="entity-label">Для писем:</span>
                    <span className="entity-value">{m.mailing_address}</span>
                  </div>
                )}
                {m.sro_info && (
                  <div className="entity-info-row">
                    <span className="entity-label">СРО:</span>
                    <span className="entity-value">{m.sro_info}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onItemsPerPageChange={(n) => {
            setItemsPerPage(n);
            setCurrentPage(1);
            apiService.saveUiSettings({ table_page_size: n }).catch(() => {});
          }}
        />
      )}

      <UniversalModal
        isOpen={modalState.isOpen}
        onClose={handleCancel}
        title={modalState.item ? (modalState.mode === 'view' ? `Управляющий: ${modalState.item.name}` : 'Редактирование управляющего') : 'Создание управляющего'}
        isViewMode={modalState.mode === 'view'}
        onEdit={() => {
          setHasUnsavedChanges(false);
          setModalState({ ...modalState, mode: 'edit' });
        }}
        hasChanges={hasUnsavedChanges}
        onConfirmClose={handleCancel}
        modalClassName="universal-modal--wide"
        disableEscClose={modalState.mode === 'edit' || confirmDialog.isOpen}
      >
        <OrganizerForm
          organizer={modalState.item}
          formData={formData}
          setFormData={setFormData}
          onSave={handleSaveManager}
          onCancel={handleCancel}
          isViewMode={modalState.mode === 'view'}
          onFormChange={setHasUnsavedChanges}
          fixedType="arbitrage_manager"
        />
      </UniversalModal>

      {confirmDialog.isOpen && (
        <ConfirmDialog
          confirmDialog={confirmDialog}
          onCancel={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
          onConfirm={() => {
            if (confirmDialog.onConfirm) {
              confirmDialog.onConfirm();
            } else {
              setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
            }
          }}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
};

const ConfirmDialog = ({ confirmDialog, onCancel, onConfirm }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onCancel();
        return false;
      }
    };

    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [onCancel]);

  return (
    <UnsavedChangesPopover
      isOpen={!!confirmDialog?.isOpen}
      title={confirmDialog?.title || 'Подтверждение'}
      message={confirmDialog?.message}
      onCancel={onCancel}
      onConfirm={onConfirm}
      cancelText={confirmDialog?.cancelText || 'Отмена'}
      confirmText={confirmDialog?.confirmText || 'Подтвердить'}
      zIndex={10000}
    />
  );
};

export default ManagersList;
